import * as Sentry from "@sentry/react"
import { Button } from "design-system/atoms/Button"
import { BadgePingText } from "design-system/molecules/BadgePingText"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "design-system/molecules/Card"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { cn } from "design-system/utils"
import config from "lib/config"
import { RotateCw } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useConnectWhatsappCoexistenceMutation } from "modules/settings/hooks/whatsapp/useConnectWhatsappCoexistenceMutation"
import { useEmbeddedSignupMutation } from "modules/settings/hooks/whatsapp/useEmbeddedSignupMutation"
import { useSyncWhatsappNumberMutation } from "modules/settings/hooks/whatsapp/useSyncWhatsappNumberMutation"
import { useWhatsappIntegrationConfigurationQuery } from "modules/settings/hooks/whatsapp/useWhatsappIntegrationConfigurationQuery"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { toast } from "sonner"
import { EmbeddedSignupSessionEventSchema } from "schema"
import {
  createEmbeddedSignupSession,
  takeEmbeddedSignupPayload,
} from "modules/settings/lib/whatsapp/embeddedSignupSession"
import { WhatsappNumbersTable } from "./WhatsappNumbersTable"
import { WhatsappTabs } from "./WhatsappTabs"

export const EmbeddedWhatsapp = () => {
  const { t } = useTranslation()
  const { data: employeeData, isPending: isEmployeePending } = useAuthEmployeeQuery()
  const [status, setStatus] = useState<FBEventData["event"] | null>(null)
  const {
    mutate: mutateEmbedded,
    isPending: isEmbeddedMutationPending,
    data: signupResult,
    reset: resetEmbedded,
  } = useEmbeddedSignupMutation()
  const { mutate: connectCoexistence, isPending: isConnectingCoexistence } =
    useConnectWhatsappCoexistenceMutation()
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>()
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>()
  const companyId =
    selectedCompanyId ??
    employeeData?.companyId ??
    (employeeData?.organization.companies.length === 1
      ? employeeData.organization.companies[0]?.id
      : undefined)
  const { data: integrationData, isPending: isIntegrationPending } =
    useWhatsappIntegrationConfigurationQuery()
  const hasHostedIntegration = !!integrationData?.hosted
  const { mutateAsync: mutateNumbersAsync, isPending: isMutateNumbersPending } =
    useSyncWhatsappNumberMutation()
  const sessionRef = useRef(createEmbeddedSignupSession())

  const attemptSignup = useCallback(() => {
    const payload = takeEmbeddedSignupPayload(sessionRef.current)
    if (!payload) return

    setStatus("PENDING")
    mutateEmbedded(payload, {
      onSuccess: () => setStatus("FINISH"),
      onError: () => {
        setStatus("ERROR")
        toast.error(t("settings.integrations.whatsapp.integrationCard.errorWhileLogin"))
      },
    })
  }, [mutateEmbedded, t])

  const launchWhatsAppSignup = useCallback(() => {
    if (!employeeData || isEmployeePending || !companyId) return

    setStatus("PENDING")
    resetEmbedded()
    setSelectedPhoneId(undefined)
    const session = createEmbeddedSignupSession(companyId)
    sessionRef.current = session

    FB.login(
      (response) => {
        if (sessionRef.current !== session) return
        if (!response?.authResponse?.code) {
          setStatus(null)
          return
        }

        session.code = response.authResponse.code
        attemptSignup()
      },
      {
        config_id: config.facebookEmbedded.configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          version: "v3",
          sessionInfoVersion: "3",
          setup: {
            business: {
              name:
                employeeData.organization.companies.find((company) => company.id === companyId)
                  ?.name ??
                employeeData.company?.name ??
                employeeData.organization.name,
              email: employeeData.email,
            },
          },
          featureType: "whatsapp_business_app_onboarding",
        },
      },
    )
  }, [employeeData, isEmployeePending, companyId, resetEmbedded, attemptSignup])

  const onMessage = useCallback(
    (event: MessageEvent) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      )
        return
      if (typeof event.data !== "string") return

      let payload: unknown
      try {
        payload = JSON.parse(event.data)
      } catch (error) {
        Sentry.captureException(new Error("Failed to parse Facebook SDK message as JSON"), {
          level: "warning",
          tags: { scope: "whatsapp:embeddedSignup" },
          extra: {
            origin: event.origin,
            errorName: error instanceof Error ? error.name : "UnknownError",
          },
        })
        return
      }

      const parsed = EmbeddedSignupSessionEventSchema.safeParse(payload)
      if (!parsed.success || sessionRef.current.isSubmitted) return
      const data = parsed.data
      sessionRef.current.event = data

      if (data.event === "FINISH" || data.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING") {
        attemptSignup()
      } else if (data.event === "ERROR") {
        sessionRef.current = createEmbeddedSignupSession()
        setStatus("ERROR")
        toast.error(t("settings.integrations.whatsapp.integrationCard.errorWhileLogin"))
      } else {
        sessionRef.current = createEmbeddedSignupSession()
        setStatus(null)
      }
    },
    [t, attemptSignup],
  )

  useEffect(() => {
    window.addEventListener("message", onMessage)
    return () => {
      window.removeEventListener("message", onMessage)
    }
  }, [onMessage])

  return (
    <>
      <WhatsappTabs />
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader className="flex flex-row gap-4 p-5">
            <div className="grow flex flex-col gap-1">
              <CardTitle>{t("settings.integrations.whatsapp.integrationCard.title")}</CardTitle>
              <CardDescription>
                {t("settings.integrations.whatsapp.integrationCard.descriptionDone")}
              </CardDescription>
            </div>
            <div className="flex items-center">
              {isIntegrationPending ? (
                <div>
                  <Loading />
                </div>
              ) : (
                <div>
                  <div className="flex gap-2 items-center">
                    <BadgePingText color={"success"}>
                      <Trans
                        i18nKey={"settings.integrations.whatsapp.integrationCard.embeddedConnected"}
                        values={{ id: integrationData?.accountId }}
                        components={{ b: <b /> }}
                      />
                    </BadgePingText>
                    {hasHostedIntegration ? (
                      <Button
                        disabled={
                          isEmbeddedMutationPending ||
                          status === "PENDING" ||
                          !companyId ||
                          !!signupResult?.needsPhoneSelection
                        }
                        onClick={launchWhatsAppSignup}
                      >
                        {t("settings.integrations.whatsapp.integrationCard.launchHosted")}
                      </Button>
                    ) : (
                      <Button
                        disabled={
                          isEmbeddedMutationPending ||
                          status === "PENDING" ||
                          !companyId ||
                          !!signupResult?.needsPhoneSelection
                        }
                        onClick={launchWhatsAppSignup}
                      >
                        {t("settings.integrations.whatsapp.integrationCard.launchDefault")}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {employeeData && employeeData.organization.companies.length > 1 && (
              <Select
                value={companyId}
                onValueChange={setSelectedCompanyId}
                disabled={status === "PENDING" || !!signupResult?.needsPhoneSelection}
              >
                <SelectTrigger aria-label={t("settings.integrations.whatsapp.table.selectCompany")}>
                  <SelectValue
                    placeholder={t("settings.integrations.whatsapp.table.selectCompany")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {employeeData.organization.companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
            {signupResult?.needsPhoneSelection && (
              <div className="flex flex-col gap-3">
                <Select
                  value={selectedPhoneId}
                  onValueChange={setSelectedPhoneId}
                  disabled={isConnectingCoexistence}
                >
                  <SelectTrigger
                    aria-label={t("settings.integrations.whatsapp.coexistence.selectNumber")}
                  >
                    <SelectValue
                      placeholder={t("settings.integrations.whatsapp.coexistence.selectNumber")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {signupResult.numbers.map((number) => (
                        <SelectItem key={number.id} value={number.id}>
                          {t("settings.integrations.whatsapp.coexistence.numberOption", number)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <Button
                  disabled={!selectedPhoneId || isConnectingCoexistence}
                  onClick={() => {
                    if (!selectedPhoneId || !sessionRef.current.companyId) return
                    connectCoexistence(
                      { phoneId: selectedPhoneId, companyId: sessionRef.current.companyId },
                      {
                        onSuccess: () => resetEmbedded(),
                        onError: () =>
                          toast.error(
                            t("settings.integrations.whatsapp.integrationCard.errorWhileLogin"),
                          ),
                      },
                    )
                  }}
                >
                  {t("settings.integrations.whatsapp.coexistence.connectNumber")}
                </Button>
                <Button
                  variant="outline"
                  disabled={isConnectingCoexistence}
                  onClick={() => {
                    resetEmbedded()
                    sessionRef.current = createEmbeddedSignupSession()
                    setStatus(null)
                  }}
                >
                  {t("settings.integrations.whatsapp.coexistence.cancelSelection")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="flex flex-col gap-4">
          <div className="flex justify-end w-full">
            <Button
              variant="outline"
              onClick={() => mutateNumbersAsync()}
              disabled={isMutateNumbersPending}
            >
              <RotateCw className={cn(isMutateNumbersPending && "animate-spin")} />
              {t("settings.integrations.whatsapp.table.reloadNumbers")}
            </Button>
          </div>
          <WhatsappNumbersTable />
        </div>
      </div>
    </>
  )
}
